export const sectionsQuestions = {
  businessDetails: {
    title: "Business details",
    description: "Let’s ease in with some basic questions about the business.",
    questionWithCoreApp: {
      ein: "Employer Identification Number (EIN)",
      isNonProfit: "Are you a non-profit?",
      hoursOfOperation: "Regular hours of operation",
      ownerHalfStakeOther: (companyName: string) =>
        `Does the majority ownership of ${companyName} also have a controlling interest in another entity?`,
      relatedEntities: {
        entityName: "Legal entity name of the related entity",
        dba: "Doing business as (DBA)",
        description: (companyName: string) =>
          `Describe the business relationship between ${companyName} and this related entity.`,
        hasW2Employees:
          "Does this related entity currently have any W-2 employees?",
        ein: "Employer Identification Number (EIN)",
        isDependent: "Are the two companies financially reliant on each other?",
        hasOverlappingEmployees: (companyName: string) =>
          `Are any employees paid by both entities or, do 2+ jobs for ${companyName}?`,
        sharesLocation: "Do the two companies share a physical work location?",
      },
    },
    questions: {
      companyName: "Legal entity name",
      dba: "Doing business as (DBA)",
      ein: "Employer Identification Number (EIN)",
      entityType: "Entity type",
      industry: "What is your company’s primary industry?",
      industryOther: "Please specify",
      industryNaics4:
        "What sector within your industry does your company operate in?",
      industryNaics6: "What specific type of work does your company do?",
      isNonProfit: "Are you a non-profit?",
      yearFounded: "What year was your company founded?",
      hoursOfOperation: "Regular hours of operation",
      ownerHalfStakeOther: (companyName: string) =>
        `Does the majority ownership of ${companyName} also have a controlling interest in another entity?`,
      relatedEntities: {
        entityName: "Legal entity name of the related entity",
        dba: "Doing business as (DBA)",
        description: (companyName: string) =>
          `Describe the business relationship between ${companyName} and this related entity.`,
        hasW2Employees:
          "Does this related entity currently have any W-2 employees?",
        ein: "Employer Identification Number (EIN)",
        isDependent: "Are the two companies financially reliant on each other?",
        hasOverlappingEmployees: (companyName: string) =>
          `Are any employees paid by both entities or, do 2+ jobs for ${companyName}?`,
        sharesLocation: "Do the two companies share a physical work location?",
      },
    },
    message: {
      companyName:
        "Legal entity name as shown on your Articles of Incorporation or other formation docs.",
      ownerHalfStakeOther:
        "A controlling interest is when the majority ownership controls over 50% of the company.",
    },
  },
  officeLocation: {
    title: "Office locations",
    description:
      "Please enter all your physical office locations starting with your Primary Business Address.",
    editCardTitlePrimary: "Primary business address",
    editCardTitleOther: "Office location",
    openEditCardPrimaryDescription:
      "This is the main location where your company conducts the bulk of your business. If you don’t have a physical office location, you can use an owner’s address.",
    closedEditCardPrimaryDescription:
      "This is the Primary Business Address we have on file for your company. Make sure everything is accurate and Edit if you need to make any changes. We’ll update the changes in any existing or pending applications.",
    questions: {
      locationName: "Name/description",
      address1: "Street 1",
      address2: "Street 2",
      city: "City",
      state: "State",
      zip: "Zip Code",
      subdivision: "County",
    },
    message: {
      address1:
        "No PO Boxes, business mailing addresses, or virtual mailboxes.",
    },
    addMoreText: "Add additional office",
  },
  owners: {
    title: "Owners",
    cardTitle: "Owner",
    description:
      "List all working owners who are providing services to your company. Please do not enter investor information.",
    questions: {
      firstName: "Legal first name",
      middleName: "Legal middle name",
      lastName: "Legal last name",
      fullName: "Full name",
      title: "Title",
      roleDescription: "Job duties",
      isPaid: "Paid or unpaid?",
      salaryType: "How are they paid?",
      salary: "Enter their salary",
      hourlyRate: "What is their hourly wage?",
      hoursWeekly: "How many hours do they work per week?",
      email: "Enter owners email",
      exemptionStatus: "Exemption status?",
      ownerStake: "Ownership percentage",
      isRemote: "Remote?",
      address1: "Street 1",
      address2: "Street 2",
      city: "City",
      state: "State",
      zip: "ZIP Code",
      officeLocationId: "Select office location",
    },
    message: {
      email: "Unless this owner is a holding company or a non-US based owner",
      firstOwnerEmail:
        "We've automatically pulled in the email that's on file. To make changes, go to your Account settings > Security/Login.",
      address1:
        "No PO Boxes, business mailing addresses, or virtual mailboxes.",
    },
    addMoreText: "Add additional owner",
  },
  employees: {
    title: "Current employees",
    cardTitle: "Employee information",
    description:
      "Tell us about the team you have right now. You need at least two employees who both reside and work in the U.S. and complete all the onboarding requirements. At least one must be a W-2 employee.",
    questions: {
      firstName: "Legal first name",
      middleName: "Legal middle name",
      lastName: "Legal last name",
      fullName: "Full name",
      employeeType: "Member type",
      title: "Title",
      department: "Department",
      workersCompensationClassification:
        "Workers' compensation classification code",
      email: "Employee email",
      adminAccess: "Will they need admin access?",
      salaryType: "How are they paid?",
      salary: "What is their annual salary?",
      hourlyRate: "What is their hourly wage?",
      hoursWeekly: "How many hours do they work per week?",
      exemptionStatus: "Exemption status?",
      isRemote: "Remote?",
      address1: "Street 1",
      address2: "Street 2",
      city: "City",
      state: "State",
      zip: "ZIP Code",
      officeLocationId: "Select office location",
    },
    message: {
      firstEmployeeEmail:
        "We've automatically pulled in the email that's on file. To make changes, go to your Account settings > Security/Login.",
      firstUserAdminAccess:
        "Whomever creates the company Justworks account is automatically considered an admin.",
      title: "Are they an Office Manager? Insurance agent? Dental assistant?",
      address1:
        "No PO Boxes, business mailing addresses, or virtual mailboxes.",
    },
    addMoreText: "Add additional employee",
  },
  census: {
    title: "Company Staff",
    firstUserQuestions: {
      isRemote: "Do you work remotely?",
      censusType: "What best describes you?",
      salaryType: "How are you paid?",
      salary: "Enter your salary",
      hourlyRate: "What is your hourly wage?",
      hoursWeekly: "How many hours do you work per week?",
      adminAccess: "Will you need admin access?",
    },
    questions: {
      firstName: "Legal first name",
      middleName: "Legal middle name",
      lastName: "Legal last name",
      fullName: "Full name",
      isRemote: "Do they work remotely?",
      address1: "Street 1",
      address2: "Street 2",
      city: "City",
      state: "State",
      zip: "ZIP Code",
      officeLocationId: "Select office location",
      censusType: "What best describes them?",
      title: "Title",
      email: "Email",
      ownerStake: "Ownership percentage",
      roleDescription: "Job duties",
      exemptionStatus: "Exemption status?",
      isPaid: "Paid or unpaid?",
      employeeType: "Member type",
      salaryType: "How are they paid?",
      salary: "Enter their salary",
      hourlyRate: "What is their hourly wage?",
      hoursWeekly: "How many hours do they work per week?",
      adminAccess: "Will they need admin access?",
    },
    message: {
      firstUserAdminAccess:
        "Whomever creates the company Justworks account is automatically considered an admin.",
      firstEmployeeEmail:
        "We've automatically pulled in the email that's on file. To make changes, go to your Account settings > Security/Login.",
      firstOwnerEmail:
        "We’ve automatically pulled in the email that’s on file. To make changes, go to your Account settings > Security/Login.",
      address1:
        "No PO Boxes, business mailing addresses, or virtual mailboxes.",
    },
  },
  employeesAttributes: {
    title: "About your team",
    description:
      "To get you a workers' compensation quote as quickly as possible, we need to understand more about your team.",
    questions: {
      travel: "Does anyone travel for work? ",
      employeesWhoTravel: {
        employeeId: "Employee Name",
        travelFrequency: "How many trips do they make per month?",
        travelInternationally: "Do they travel outside of the US?",
        travelLocation: "Which countries do they travel to?",
      },
      hardware: "Does anyone work with tools or equipment?",
      employeesWhoUseTools: {
        employeeId: "Employee Name",
        hardwareNames: "Tools used",
      },
      employeesPhysicalLabor: "Is physical labor required from your team?",
      employeesWhoRequirePhysicalLabor: {
        employeeId: "Employee Name",
        percentPhysicalLabor: "For what percentage of their day?",
      },
      hasContractors:
        "Do you work with any U.S. based, 1099 independent contractors?",
      employmentDuration: "What is the duration of their employment?",
      numberContractors: "How many do you work with annually?",
      percentContractors:
        "What percentage of your total payroll goes to these contractors?",
      contractorWorkType: "Describe their work",
      contractorsHaveWorkComp:
        "Do you require independent contractors to maintain their own Workers' Compensation insurance?",
      contractorsHaveWorkersCompCois:
        "Do you collect these certificates of insurance (COI) to verify proof of coverage?",
      numberTemp: "How many seasonal/temporary workers do you employ?",
      boatsBridges:
        "Does anyone work near a body of water, like on a bridge, dock, or barge?",
      employeesWorkUnderground: "Does anyone work underground?",
      employeesWhoWorkUnderground: {
        employeeId: "Employee Name",
        undergroundDescription: "Describe their work",
      },
      employeesWorkOnLadders: "Does anyone work on ladders?",
      employeesWhoWorkOnLadders: {
        employeeId: "Employee Name",
        laddersDescription: "Describe their work",
      },
      employeePerformHeavyLifting: "Does anyone perform any heavy lifting?",
      employeesWhoPerformHeavyLifting: {
        employeeId: "Employee Name",
        maximumWeightLifted:
          "What is the maximum weight lifted by teammates unassisted?",
      },
      deliveries: "Does anyone make deliveries?",
      employeesWhoMakeDeliveries: {
        employeeId: "Employee Name",
        deliveryTransportationType: "What type of vehicle is used?",
        deliveryDistance: "What is the typical mile radius of deliveries?",
        deliveryFrequency: "How frequently do they make deliveries?",
      },
      physicalDisability: "Does anyone have a physical disability?",
      underageOrSenior: "Is anyone under the age of 17 or over 60?",
      underageOrSeniorEmployees: {
        employeeId: "Employee Name",
        dateOfBirth: "Date of birth",
        jobTitle: "Job title",
      },
    },
    message: {
      travel:
        "This can include things like networking events, client meetings, or visiting a vendor.",
      maximumWeightLifted: "If over 50 lbs, please describe",
    },
  },
  aboutYourBusiness: {
    title: "About your business",
    description:
      "A business description gives us an overall understanding of your company and industry.",
    questions: {
      businessDescription: "Describe your core business activities",
      website: "Company websites & links",
      verifyCompanyUrl: "Attach any relevant documents to verify the business",
      dailyOperationsOfEmployees:
        "What is a typical day-to-day operation for your business?",
      hasUnionWorkers:
        "Are any of your employees currently covered by a union collective bargaining agreement or do you anticipate any will be?",
    },
    message: {
      businessDescription:
        "Tell us about the primary products or services you offer, how you attract and serve customers, and the main ways you generate revenue.",
      companyUrl:
        "This will help us verify your business. Add your company website (include https:// or http://), company or employee Linkedin pages, and any other online presence.",
      dailyActivities:
        "Tell us about the specific tasks performed by your employees and their work environment.",
    },
    placeholder: {
      businessDescription:
        "We run an online learning platform that offers self-paced courses, live sessions, and certifications. We attract users through free trials and we generate revenue with subscriptions.",
      dailyActivities:
        "Instructors create content, engineers enhance features, and marketing drives engagement. We operate remotely with daily collaboration and flexible schedules.",
    },
  },
  businessAttributes: {
    title: "Business Attributes",
    description:
      "This section will help us understand more about your business activities.",
    questions: {
      hasPriorCarriers:
        "Do you currently have a workers' compensation policy or have you had one in the past?",
      priorCarriers: "Which carriers have you used?",
      hasHadWorkersCompPolicy:
        "Do you currently or have you in the past 5 years had any claims against your workers compensation insurance?",
      beenCancelled: "Have any of your policies ever been canceled?",
      policyCancelledReason: "Please explain",
      hasPastDuePremiums:
        "Do you have any past due workers' compensation insurance premiums?",
      beenDeclined:
        "Has your company ever been declined for workers' compensation?",
      sponsorSportsTeams: "Do you sponsor employee athletic teams?",
      hasTaxLeinBankrupcy:
        "Have you had tax liens or bankruptcy within the last 5 years? ",
      offerHealthInsurance: "Do you offer your employees health insurance?",
      ownAircraftWatercraft:
        "Do you own, operate, or lease aircraft/watercraft?",
      use3rdPartyVendors: "Do you use 3rd party vendors/manufacturers?",
      thirdPartyDetails1: "What kind of work do they do for you?",
      thirdPartyDetails2: "Where are they located?",
      thirdPartyDetails3:
        "Describe the business arrangements you have with them.",
      thirdPartyDetails4:
        "Do employees visit any third party vendors or manufacturers?",
      hasVolunteers: "Do you have volunteers who work for your company? ",
      numberVolunteers: "How many?",
      volunteerActivities: "Describe their work",
      hasVolunteerAccidentPolicy:
        "Do you currently have or plan to have a volunteer accident policy in place?",
      volunteerAccidentPolicy:
        "If yes, could you provide a copy of your policy?",
      volunteerTravel: "Are your volunteers required to travel?",
      hazardousMats: "Do you work with hazardous materials?",
      materialSafetyDataSheets:
        "Attach copies of your Material Safety Data Sheets (MSDS or SDS) if available",
      hasWrittenSafteyProg:
        "Do you have a written safety program in operation?",
    },
    message: {
      thirdPartyDetails1:
        "No need to include 1099 individual independent contractors",
    },
  },
  documents: {
    title: "Attach documents",
    description:
      "  See below for a list of the required documentation that we will need from you. Please only include PDFs.",
    questions: {
      incorporationDocuments: "Entity formation documents",
      lossRunsReport: "Loss Run Report",
      noLossRunsReportReason: "Don’t have a Loss Run Report? Let us know why:",
      noLossRunsReportOtherReason: "Please explain",
    },
    message: {
      incorporationDocuments:
        "Articles/certificates are a common way to show that a business entity exists. These may be called Articles of Incorporation, Partnership Agreements, Operating Agreements, or Articles of Formation. You should already have a copy of your entity formation documents, but if you can’t find it, you can request a new one from the applicable state.",
      lossRunsReport:
        "This document contains information about your existing workers’ compensation policy, including any claims (losses) you may have had during the past five years. You can obtain this by contacting your current workers’ compensation carrier.",
    },
  },
  epli: {
    title: "Employment Practice Liability Insurance (EPLI)",
    description:
      "EPLI provides coverage for claims made by employees, former employees, or potential employees for discrimination, wrongful termination, harassment, and certain other employment-related issues.",
    questions: {
      hasPolicy: "Does your company have an EPLI policy? ",
      epliDocument: "Please attach a valid EPLI certificate of insurance",
      epliLossDocument: "EPLI Loss Run Report",
      noEpliLossRunsReportReason:
        "Don’t have an EPLI Loss Run Report? Let us know why:",
      noEpliLossRunsReportOtherReason: "Please explain",
      receivedEpliAllegations:
        "Has your company or any of its subsidiaries received any allegations or claims under any policy that provides EPLI-type coverage (e.g., EPLI, commercial general liability, or any other employment practices policy) in the past 3 years (either your own policy, or a policy provided by your current PEO, if applicable)?",
      beenSuedFormerEmp:
        "Regardless of whether covered by EPLI-type coverage, has your company or any of its subsidiaries been sued by a current or former employee, independent contractor, or officer in the last 12 months? ",
      claimDetails: "Please explain why and include any losses paid",
      overTwentyFiveEe: "Does your company have 25+ US-based employees?",
      offeredSeverancePackages:
        "If yes, will severance packages be offered to employees who are laid off in exchange for a release not to sue?",
    },
    message: {
      epliLossDocument:
        "This document contains information about your existing EPLI policy, including any claims (losses) you may have had during the past five years. You can obtain this by contacting your current EPLI carrier.",
    },
  },
  // TODO: add landing page, review page and congrats page copy here once we fix ReviewAndSubmit component
};
